// #region imports

// --- Angular
import { Injectable } from '@angular/core';

// --- RxJs
import { Subscription } from 'rxjs';

// --- 3rd Party
import { User as OidcUser } from 'oidc-client';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { SurveyStatuses, SurveyResponseModel } from '../../../models';
import { OidcService } from '../account/oidc.service';
import { SurveyBreakBaseService } from './survey-break-base.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class SurveyBreakService extends SurveyBreakBaseService {
  private _identitySubscription: Subscription;
  private _identity: OidcUser | null = null;

  constructor(
    private translate: TranslateService,
    private oidcService: OidcService) {
    super();

    this._identitySubscription = this.oidcService.identity$.subscribe((identity) => {
      this._identity = identity;
    });
  }

  destructor() {
    this._identitySubscription.unsubscribe();
    this._identitySubscription = Subscription.EMPTY;
  }

  loadContent(identity: OidcUser, surveyResponse: SurveyResponseModel) {
    return [
      {
        heading: this.translate.instant('surveyBreakHeading1', { name:  identity.profile.given_name}),
        subHeading: this.translate.instant('surveyBreakP1', { progress: 15 }),
        waitText: this.translate.instant('surveyBreakWaitP1'),
        surveyStatus: SurveyStatuses.indicatorQuestions,
        groupIndex: 1,
      },
      {
        heading: this.translate.instant('surveyBreakHeading2', { name:  identity.profile.given_name}),
        subHeading: this.translate.instant('surveyBreakP2', { progress: 30 }),
        waitText: this.translate.instant('surveyBreakWaitP1'),
        surveyStatus: SurveyStatuses.indicatorQuestions,
        groupIndex: 2,
      },
      {
        heading: this.translate.instant('surveyBreakHeading3', { name:  identity.profile.given_name}),
        subHeading: this.translate.instant('surveyBreakP3', { progress: 45 }),
        waitText: this.translate.instant('surveyBreakWaitP1'),
        surveyStatus: SurveyStatuses.indicatorQuestions,
        groupIndex: 3,
      },
      {
        heading: this.translate.instant('surveyBreakHeading4', { name:  identity.profile.given_name}),
        subHeading: this.translate.instant('surveyBreakP4', { progress: 60 }),
        waitText: this.translate.instant('surveyBreakWaitP1'),
        surveyStatus: SurveyStatuses.indicatorQuestions,
        groupIndex: 4,
      },
      {
        heading: this.translate.instant('surveyBreakHeading5', { name:  identity.profile.given_name}),
        subHeading: this.translate.instant('surveyBreakP5'),
        waitText: this.translate.instant('surveyBreakWaitP1'),
        surveyStatus: SurveyStatuses.effectivenessQuestions,
        groupIndex: 5,
      },
      {
        heading: this.translate.instant('surveyBreakLastHeading', { name:  identity.profile.given_name}),
        subHeading: this.translate.instant('surveyBreakLastP1'),
        waitText: this.translate.instant('surveyBreakLastWaitP1'),
        surveyStatus: SurveyStatuses.commentsQuestions,
        groupIndex: 5,
      },
      {
        heading: this.translate.instant('surveyBreakHeading7', { name:  identity.profile.given_name}),
        subHeading: this.translate.instant('surveyBreakP7'),
        waitText: surveyResponse
          && surveyResponse.hasExtraValueQuestions
          ? this.translate.instant('surveyBreakWaitP1')
          : this.translate.instant('surveyBreakLastWaitP1'),
        surveyStatus: SurveyStatuses.effectivenessQuestions,
        groupIndex: 6,
      },
      {
        heading: this.translate.instant('surveyBreakLastHeading', { name:  identity.profile.given_name}),
        subHeading: this.translate.instant('surveyBreakLastP1'),
        waitText: this.translate.instant('surveyBreakLastWaitP1'),
        surveyStatus: SurveyStatuses.extraValueQuestions,
        groupIndex: 7,
      }
    ];
  }

  loadBySurveyStatus(surveyStatus: SurveyStatuses): void {
    if (!this._contentList) return;

    const exists = this._contentList.find(c => c.surveyStatus === surveyStatus);
    if (exists) {
      this._activeContent$.next(exists);
      return;
    }

    this._activeContent$.next(this._contentList[0]);
  }

  loadBySurveyStatusAndGroupIndex(surveyResponse: SurveyResponseModel, groupIndex: number): void {
    if (!this._identity) return;

    const exists = this.loadContent(this._identity, surveyResponse)
      .find(c => c.surveyStatus === surveyResponse.status && c.groupIndex === (groupIndex + 1));
    if (exists) {
      this._activeContent$.next(exists);
      return;
    }

    this.loadBySurveyStatus(surveyResponse.status);
  }
}
